import { useEffect, useState } from "react";
import { CheckCircle2, TriangleAlert, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const osName = (ua) => {
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "iPhone/iPad";
  if (/Android/.test(ua)) return "Android";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac OS X|Macintosh/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "this device";
};

const browserName = (ua) => {
  if (navigator.brave) return "Brave";
  if (/Edg(A|iOS)?\//.test(ua)) return "Edge";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/SamsungBrowser/.test(ua)) return "Samsung Internet";
  if (/Firefox|FxiOS/.test(ua)) return "Firefox";
  if (/Chrome\/|CriOS/.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return "This browser";
};

export const detectBrowser = () => {
  const ua = navigator.userAgent;
  return { browser: browserName(ua), os: osName(ua), bluetooth: Boolean(navigator.bluetooth), usb: Boolean(navigator.usb) };
};

// What to tell someone whose browser can't use Bluetooth yet.
const bluetoothFix = ({ browser, os }) => {
  if (os === "iPhone/iPad") return "iPhone and iPad browsers can't use Bluetooth for websites. You can still set up and save robots here; connect from a computer, Chromebook or Android tablet.";
  if (browser === "Brave") return "Brave switches Bluetooth off for websites. Set brave://flags/#brave-web-bluetooth-api to Enabled and restart Brave.";
  if (os === "Linux" && browser === "Chrome") return "On Linux, turn on chrome://flags/#enable-experimental-web-platform-features and restart Chrome.";
  return "You can still set up and save robots here. To connect to hubs, open HubPilot in Chrome or Edge.";
};

const Check = ({ ok, warn, label, testId }) => {
  const Icon = ok ? CheckCircle2 : warn ? TriangleAlert : XCircle;
  return (
    <li className="flex items-center gap-2 text-sm" data-testid={testId} data-ok={ok}>
      <Icon className={cn("h-4 w-4 shrink-0", ok ? "text-primary" : warn ? "text-accent" : "text-destructive")} />{label}
    </li>
  );
};

export const BrowserCheck = () => {
  const [env] = useState(detectBrowser);
  const [adapter, setAdapter] = useState(null);
  useEffect(() => {
    navigator.bluetooth?.getAvailability?.().then(setAdapter).catch(() => setAdapter(null));
  }, []);

  const ready = env.bluetooth && adapter !== false;
  const headline = ready
    ? env.usb ? "connect to hubs and install firmware" : "connect to hubs and drive"
    : env.bluetooth ? "connect once Bluetooth is on" : "set up robots, but not connect to hubs";

  return (
    <div className={cn("rounded-3xl border-2 bg-card p-6", ready ? "border-primary" : "border-accent")} data-testid="browser-check" data-ready={ready}>
      <p className="eyebrow text-muted-foreground">Your browser right now</p>
      <p className="mt-2 font-display text-xl font-bold sm:text-2xl" data-testid="browser-check-headline">
        {env.browser} on {env.os} can {headline}.
      </p>
      <ul className="mt-4 grid gap-2 sm:grid-cols-3">
        <Check ok label="Set up and save robots" testId="browser-check-code" />
        <Check ok={ready} warn={env.bluetooth} label={adapter === false ? "Bluetooth is off or missing" : "Connect to hubs (Bluetooth)"} testId="browser-check-bluetooth" />
        <Check ok={env.usb} label="Install firmware (USB)" testId="browser-check-usb" />
      </ul>
      {!env.bluetooth && <p className="mt-4 text-sm text-muted-foreground" data-testid="browser-check-fix">{bluetoothFix(env)}</p>}
      {env.bluetooth && adapter === false && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="browser-check-fix">Switch Bluetooth on in your computer's settings, or plug in a Bluetooth 4.0+ adapter, then reload this page.</p>
      )}
      {ready && !env.usb && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="browser-check-fix">To install firmware, use Chrome or Edge on a computer or Chromebook.</p>
      )}
    </div>
  );
};
